import { Tag, TagGroups, tagWeights } from "types";
import jsonData from "assets/json/Tags.json";

export class TagHandling {
  private allowedTagGroups: TagGroups;
  private allowedGroupNames: string[] | null;

  constructor(allowedGroups: string[] | null = null) {
    this.allowedGroupNames = allowedGroups;
    this.allowedTagGroups = this.initializeTagGroups();
  }

  private initializeTagGroups(): TagGroups {
    if (this.allowedGroupNames === null) {
      return Object.entries(jsonData).reduce((acc, [group, groupData]) => {
        acc[group] = this.processNestedStructure(groupData, group);
        return acc;
      }, {} as TagGroups);
    }

    return Object.entries(jsonData)
      .filter(([group]) => this.allowedGroupNames?.includes(group))
      .reduce((acc, [group, groupData]) => {
        acc[group] = this.processNestedStructure(groupData, group);
        return acc;
      }, {} as TagGroups);
  }

  private processNestedStructure(data: any, group: string): Array<Tag> {
    const tags: Array<Tag> = [];

    Object.entries(data).forEach(([level2, level2Data]) => {
      tags.push({ tag: level2, group });

      if (Array.isArray(level2Data)) {
        level2Data.forEach((item) => {
          tags.push({ tag: item, group, level2Category: level2 });
        });
      } else {
        Object.entries(level2Data as object).forEach(([level3, level3Data]) => {
          tags.push({ tag: level3, group, level2Category: level2 });

          if (Array.isArray(level3Data)) {
            level3Data.forEach((item) => {
              tags.push({
                tag: item,
                group,
                level2Category: level2,
                level3Category: level3,
              });
            });
          }
        });
      }
    });

    return tags;
  }

  public getAllowedTags(): string[] {
    return Object.values(this.allowedTagGroups).flatMap((group) =>
      group.map((tag) => tag.tag)
    );
  }

  public isTagAllowed(tag: string): boolean {
    return this.getAllowedTags().includes(tag);
  }

  public getTagsByGroup(group: string): string[] {
    return (this.allowedTagGroups[group] || []).map((tag) => tag.tag);
  }

  public getAllTagGroups(): string[] {
    return Object.keys(this.allowedTagGroups);
  }

  public getRelatedCategories(tag: Tag): string[] {
    const categories = [tag.tag];
    if (tag.level3Category) categories.push(tag.level3Category);
    if (tag.level2Category) categories.push(tag.level2Category);
    return categories;
  }

  public getTagWeight(tag: string): number {
    const group = Object.entries(this.allowedTagGroups).find(([_, tags]) =>
      tags.some((t) => t.tag === tag)
    )?.[0];
    return group ? tagWeights[group] || 1 : 1;
  }
}
